import { useNavigate, useLocation } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Button,
} from '@mui/material'
import {
  ArrowBack,
  DashboardCustomizeSharp,
  HomeSharp,
  TabletAndroidRounded,
  Forklift,
  CloudSyncOutlined,
} from '@mui/icons-material'
import ApexLogo from '../../assets/apex-logo.svg'
import { zIndexes } from '@/utils/zIndexes'

interface SidePanelProps {
  open: boolean
  onClose: () => void
}

// Menu items configuration for the side panel
const sideMenuItems = [
  { id: 'overview', text: 'Overview', icon: HomeSharp, path: '/' },
  {
    id: 'apex-client',
    text: 'Apex Client',
    icon: DashboardCustomizeSharp,
    path: '/apex-client',
  },
  {
    id: 'dispatch-mobile',
    text: 'Dispatch Mobile',
    icon: TabletAndroidRounded,
    path: '/dispatch-mobile',
  },
  {
    id: 'dock-automation',
    text: 'Dock Automation',
    icon: Forklift,
    path: '/dock-automation',
  },
  {
    id: 'cloud-watch',
    text: 'CloudWatch',
    icon: CloudSyncOutlined,
    path: '/cloud-watch',
  },
]

export default function SidePanel({ open, onClose }: SidePanelProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  const isRouteActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      transitionDuration={225}
      sx={{
        width: 300,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 300,
          boxSizing: 'border-box',
          borderRight: '1px solid #E0E0E0',
          backgroundColor: '#FFFFFF',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
          zIndex: zIndexes.sidePanel,
          transition: 'transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header with APEX Logo and SENTRY title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingTop: '20px',
          px: 2.5,
          backgroundColor: '#FFFFFF',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <img src={ApexLogo} alt="APEX Logo" style={{ height: '80px', width: 'auto' }} />
          <Typography
            sx={{
              color: '#026AB7',
              fontWeight: 700,
              fontSize: '20px',
              letterSpacing: '0.8px',
              marginTop: '18px',
            }}
          >
            SENTRY
          </Typography>
        </Box>
      </Box>

      {/* Menu Items - Flexible scrollable area */}
      <Box sx={{ overflow: 'auto', py: 2, flexGrow: 1 }}>
        <List sx={{ px: 2 }}>
          {sideMenuItems.map((item) => {
            const IconComponent = item.icon
            const isSelected = isRouteActive(item.path)

            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 1.25,
                    px: 1.75,
                    gap: '12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: isSelected ? '#D6EBFF' : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? '#C4E0FF' : '#EEEEEE',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#D6EBFF !important',
                      '&:hover': {
                        backgroundColor: '#C4E0FF !important',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: isSelected ? '#026AB7' : '#616161',
                    }}
                  >
                    <IconComponent sx={{ fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '17px',
                        letterSpacing: '0.15px',
                        lineHeight: '1.5',
                        fontStyle: 'bold',
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? '#026AB7' : '#000000DE',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>

      {/* Collapse section at the bottom */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 2.5,
          py: 2,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={onClose}
          startIcon={<ArrowBack sx={{ fontSize: 24 }} />}
          sx={{
            py: 1,
            borderRadius: '6px',
            transition: 'all 0.2s ease-in-out',
            textTransform: 'none',
            color: '#757575',
            '&:hover': {
              backgroundColor: '#EEEEEE',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: '17px',
              letterSpacing: '0.15px',
              lineHeight: '1.5',
              fontWeight: 500,
              color: '#757575',
            }}
          >
            Collapse
          </Typography>
        </Button>
      </Box>
    </Drawer>
  )
}
